class VariantLinksSlider extends HTMLElement {
    constructor() {
      super();
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onPrefetchIntent = this.onPrefetchIntent.bind(this);
      this.prefetchedUrls = new Set();

      // Browsers with speculation rules prefetch these links from the rules in
      // snippets/variant-links.liquid; the rest fall back to <link rel="prefetch">.
      this.usePrefetchFallback = !(HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'));
    }

    connectedCallback() {
      this.addEventListener('focusin', this.onFocusIn);

      if (this.usePrefetchFallback) {
        ['pointerover', 'focusin'].forEach((type) => this.addEventListener(type, this.onPrefetchIntent));
        this.addEventListener('touchstart', this.onPrefetchIntent, { passive: true });
      }

      this.initSwiper();
    }

    disconnectedCallback() {
      this.removeEventListener('focusin', this.onFocusIn);
      ['pointerover', 'focusin', 'touchstart'].forEach((type) => this.removeEventListener(type, this.onPrefetchIntent));
      this.destroySwiper();
    }

    onPrefetchIntent(event) {
      const link = event.target.closest('a.variant-card');
      if (!link || this.prefetchedUrls.has(link.href) || navigator.connection?.saveData) return;

      this.prefetchedUrls.add(link.href);

      const prefetch = document.createElement('link');
      prefetch.rel = 'prefetch';
      prefetch.href = link.href;
      document.head.appendChild(prefetch);
    }

    // Swiper 7 doesn't bring a focused slide into view, so keyboard users
    // tabbing through the cards would lose sight of them off-screen.
    onFocusIn(event) {
      if (!this.swiperInstance) return;

      const slide = event.target.closest('.swiper-slide');
      const index = this.swiperInstance.slides.indexOf(slide);

      this.scrollLeft = 0;
      if (index > -1) this.swiperInstance.slideTo(index);
    }

    // Only move the row if the current product's card would start off-screen,
    // and leave the card before it in view so it's clear the row scrolls back.
    initialSlideIndex() {
      const slides = [...this.querySelectorAll('.swiper-slide')];
      const activeIndex = slides.findIndex((slide) => slide.querySelector('[aria-current="page"]'));
      if (activeIndex < 1) return 0;

      const activeSlide = slides[activeIndex];
      const isInView = activeSlide.offsetLeft + activeSlide.offsetWidth <= this.clientWidth;

      return isInView ? 0 : activeIndex - 1;
    }

    initSwiper() {
      if (this.swiperInstance || !window.swiper) return;

      this.swiperInstance = new window.swiper(this, {
        slidesPerView: 'auto',
        spaceBetween: 10,
        freeMode: true,
        watchOverflow: true,
        initialSlide: this.initialSlideIndex(),
        // Swiper's a11y module relabels each slide "1 / 6" and swaps the
        // list items' role for "group", hiding the card names and the list.
        a11y: {
          enabled: false,
        },
        scrollbar: {
          el: this.querySelector('.swiper-scrollbar'),
          draggable: true,
        },
      });
    }

    destroySwiper() {
      if (!this.swiperInstance) return;

      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  if (!customElements.get('variant-links-slider')) {
    customElements.define('variant-links-slider', VariantLinksSlider);
  }
